// Dashboard display components — StatCard, ModuleCard, ChapterCard, TopicCard, CognitiveBar.
//
// Extracted from course-dashboard.tsx so the main view focuses on layout and
// composition. Each component is pure (props in, JSX out) with no store access.
//
// Session 46: View monolith extraction.

import { Link } from 'react-router-dom';
import {
  Layers,
  AlertTriangle,
  Bookmark,
  ListChecks,
  CheckSquare,
  Compass,
  BookOpenCheck,
  ScrollText,
  FileText,
  Sigma,
} from 'lucide-react';
import type { CognitiveFamily, CognitiveLevel, ComponentId } from '@examforge/shared/schemas';
import { COMPONENT_MANIFEST } from '@examforge/shared';
import type { ChapterStats, TopicStats, CognitiveDistribution } from '../../lib/stats.js';
import { BreakdownPanel, type BreakdownRowDisplay } from '../BreakdownPanel.js';
import { ChapterRegenButton } from '../chapter-regen-button.js';
import type { StageOption } from '../../lib/regen-utils.js';
import type { DictionaryKey } from '@examforge/shared/i18n';
import type { TFunction } from '../../i18n/use-t.js';

// ── StatCard ────────────────────────────────────────────────────────────────

export function StatCard({
  icon, tone, value, label, sub,
}: {
  icon: React.ReactNode;
  tone: 'primary' | 'warning' | 'accent' | 'muted';
  value: number;
  label: string;
  sub?: string;
}) {
  return (
    <div className={`dashboard-stat dashboard-stat-${tone}`}>
      <div className="dashboard-stat-icon" aria-hidden="true">{icon}</div>
      <div className="dashboard-stat-body">
        <span className="dashboard-stat-value">{value}</span>
        <span className="dashboard-stat-label">{label}</span>
        {sub && <span className="dashboard-stat-sub">{sub}</span>}
      </div>
    </div>
  );
}

// ── ModuleCard ──────────────────────────────────────────────────────────────

export function ModuleCard({
  to, icon, label, statValue, statLabel, cta,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  statValue: number;
  statLabel: string;
  cta: string;
}) {
  return (
    <Link to={to} className="dashboard-module-card">
      <span className="dashboard-module-head">
        <span className="dashboard-module-icon" aria-hidden="true">{icon}</span>
        <span className="dashboard-module-name">{label}</span>
      </span>
      <span className="dashboard-module-stat">
        <span className="dashboard-module-stat-value">{statValue}</span>
        <span className="dashboard-module-stat-label">{statLabel}</span>
      </span>
      <span className="dashboard-module-cta">{cta} →</span>
    </Link>
  );
}

// ── Breakdown row configs ───────────────────────────────────────────────────

// ChapterChip — one component-type entry inside a ChapterCard breakdown.
// Manifest-driven: add new component → append a row to BREAKDOWN_ROWS below.
//
// Session 19AB · §13: each row has a `family` (single source = manifest).
// Expanded view groups rows by family in FAMILY_ORDER, gives users
// "认知驱动业务逻辑" view (学材料 / 测自己 / 论述演练 / 模拟考场).

interface BreakdownRow {
  family: CognitiveFamily;
  show: (s: ChapterStats) => boolean;
  to: (s: ChapterStats) => string | null; // null → no link (read-only count)
  icon: React.ReactNode;
  count: (s: ChapterStats) => string; // "3/5" | "8" | "8 due"
  labelKey: DictionaryKey;
}

export const BREAKDOWN_ROWS: readonly BreakdownRow[] = [
  // Family A · Generative — 学材料
  {
    family: 'generative',
    show: (s) => s.totalFlashcards > 0,
    to: (s) => `flashcards?case=${s.chapterId}`,
    icon: <Layers size={12} aria-hidden="true" />,
    count: (s) => `${s.dueFlashcards}/${s.totalFlashcards}`,
    labelKey: 'nav.flashcards',
  },
  {
    family: 'generative',
    show: (s) => s.totalMindmaps > 0,
    to: () => 'mindmap',
    icon: <Compass size={12} aria-hidden="true" />,
    count: (s) => String(s.totalMindmaps),
    labelKey: 'nav.mindmap',
  },
  {
    family: 'generative',
    show: (s) => s.totalGlossary > 0,
    to: () => 'glossary',
    icon: <BookOpenCheck size={12} aria-hidden="true" />,
    count: (s) => String(s.totalGlossary),
    labelKey: 'nav.glossary',
  },
  {
    family: 'generative',
    show: (s) => s.totalLectureNotes > 0,
    to: () => 'lecture-notes',
    icon: <FileText size={12} aria-hidden="true" />,
    count: (s) => String(s.totalLectureNotes),
    labelKey: 'dashboard.itemKindNote',
  },
  // Family B · Discrimination — 测自己
  {
    family: 'discrimination',
    show: (s) => s.totalMcqs > 0,
    to: (s) => `quiz?case=${s.chapterId}`,
    icon: <ListChecks size={12} aria-hidden="true" />,
    count: (s) => String(s.totalMcqs),
    labelKey: 'nav.quiz',
  },
  {
    family: 'discrimination',
    show: (s) => s.totalTfs > 0,
    to: (s) => `quiz?case=${s.chapterId}&kind=tf`,
    icon: <CheckSquare size={12} aria-hidden="true" />,
    count: (s) => String(s.totalTfs),
    labelKey: 'dashboard.itemKindTf',
  },
  {
    family: 'discrimination',
    show: (s) => s.totalProofDrills > 0,
    to: () => 'proof-drills',
    icon: <Sigma size={12} aria-hidden="true" />,
    count: (s) => String(s.totalProofDrills),
    labelKey: 'nav.proofDrill',
  },
  // Family B · Discrimination (English reading · Session 19AN)
  {
    family: 'discrimination',
    show: (s) => s.totalReadingPassages > 0,
    to: () => 'reading',
    icon: <FileText size={12} aria-hidden="true" />,
    count: (s) => String(s.totalReadingPassages),
    labelKey: 'nav.readingPassage',
  },
  // Family C · Synthesis — 论述演练
  {
    family: 'synthesis',
    show: (s) => s.totalEssays > 0,
    to: () => 'essay',
    icon: <ScrollText size={12} aria-hidden="true" />,
    count: (s) => `${s.essayDrafted}/${s.totalEssays}`,
    labelKey: 'nav.essay',
  },
  {
    family: 'synthesis',
    show: (s) => s.totalFreeResponses > 0,
    to: () => 'free-response',
    icon: <ScrollText size={12} aria-hidden="true" />,
    count: (s) => String(s.totalFreeResponses),
    labelKey: 'nav.freeResponse',
  },
];

// Topic-level breakdown rows — mirrors BREAKDOWN_ROWS but operates on TopicStats.
// Each row's `to` builds a `?topic=<id>` query string.
// Manifest-driven: add component → append row.
interface TopicBreakdownRow {
  family: CognitiveFamily;
  show: (s: TopicStats) => boolean;
  to: (s: TopicStats) => string | null;
  icon: React.ReactNode;
  count: (s: TopicStats) => string;
  labelKey: DictionaryKey;
}

export const TOPIC_BREAKDOWN_ROWS: readonly TopicBreakdownRow[] = [
  { family: 'generative',    show: (s) => s.totalFlashcards > 0,      to: (s) => `flashcards?topic=${encodeURIComponent(s.topicId)}`, icon: <Layers size={12} aria-hidden="true" />,        count: (s) => `${s.dueFlashcards}/${s.totalFlashcards}`, labelKey: 'nav.flashcards' },
  { family: 'generative',    show: (s) => s.totalMindmaps > 0,        to: ()  => 'mindmap',                                          icon: <Compass size={12} aria-hidden="true" />,        count: (s) => String(s.totalMindmaps),                  labelKey: 'nav.mindmap' },
  { family: 'generative',    show: (s) => s.totalGlossary > 0,        to: ()  => 'glossary',                                         icon: <BookOpenCheck size={12} aria-hidden="true" />,  count: (s) => String(s.totalGlossary),                  labelKey: 'nav.glossary' },
  { family: 'generative',    show: (s) => s.totalLectureNotes > 0,    to: ()  => null,                                               icon: <FileText size={12} aria-hidden="true" />,       count: (s) => String(s.totalLectureNotes),              labelKey: 'dashboard.itemKindNote' },
  { family: 'discrimination',show: (s) => s.totalMcqs > 0,            to: (s) => `quiz?topic=${encodeURIComponent(s.topicId)}`,      icon: <ListChecks size={12} aria-hidden="true" />,     count: (s) => String(s.totalMcqs),                      labelKey: 'nav.quiz' },
  { family: 'discrimination',show: (s) => s.totalTfs > 0,             to: (s) => `quiz?topic=${encodeURIComponent(s.topicId)}&kind=tf`, icon: <CheckSquare size={12} aria-hidden="true" />,    count: (s) => String(s.totalTfs),                       labelKey: 'dashboard.itemKindTf' },
  { family: 'discrimination',show: (s) => s.totalProofDrills > 0,     to: ()  => null,                                               icon: <Sigma size={12} aria-hidden="true" />,          count: (s) => String(s.totalProofDrills),               labelKey: 'nav.proofDrill' },
  { family: 'discrimination',show: (s) => s.totalReadingPassages > 0, to: ()  => 'reading',                                          icon: <FileText size={12} aria-hidden="true" />,       count: (s) => String(s.totalReadingPassages),           labelKey: 'nav.readingPassage' },
  { family: 'synthesis',     show: (s) => s.totalEssays > 0,          to: ()  => 'essay',                                            icon: <ScrollText size={12} aria-hidden="true" />,     count: (s) => String(s.totalEssays),                    labelKey: 'nav.essay' },
  { family: 'synthesis',     show: (s) => s.totalFreeResponses > 0,   to: ()  => 'free-response',                                    icon: <ScrollText size={12} aria-hidden="true" />,     count: (s) => String(s.totalFreeResponses),             labelKey: 'nav.freeResponse' },
];

// ── ChapterCard ─────────────────────────────────────────────────────────────

export function ChapterCard({
  stats, t, courseId, enabledStages, onRegenComplete,
}: {
  stats: ChapterStats;
  t: TFunction;
  /** When provided, renders a chapter-scoped regen button in the header
   *  (19BJ Session 5 — content regen, distinct from per-card settings). */
  courseId?: string;
  enabledStages?: StageOption[];
  onRegenComplete?: () => void;
}) {
  const masteryShown = stats.mastery !== null ? `${stats.mastery}%` : '—';
  const breakdownRows: BreakdownRowDisplay[] = BREAKDOWN_ROWS
    .filter((r) => r.show(stats))
    .map((r) => ({
      family: r.family,
      icon: r.icon,
      label: t(r.labelKey),
      count: r.count(stats),
      to: r.to(stats),
      key: r.labelKey,
    }));
  const totalEssayProgress = stats.totalEssays > 0
    ? Math.round((stats.essayDrafted / stats.totalEssays) * 100)
    : null;

  return (
    <div className="chapter-card" style={{ borderLeftColor: stats.color }}>
      <header className="chapter-card-head">
        <span className="chapter-card-name" title={stats.chapterName}>{stats.chapterName}</span>
        {courseId && (
          <ChapterRegenButton
            courseId={courseId}
            chapterId={stats.chapterId}
            enabledStages={enabledStages ?? []}
            onRegenComplete={onRegenComplete}
          />
        )}
        <span className={`chapter-card-mastery ${masteryClass(stats.mastery)}`}>
          {masteryShown}
        </span>
      </header>
      <div className="chapter-card-bar">
        <div
          className={`chapter-card-bar-fill ${masteryClass(stats.mastery)}`}
          style={{ width: `${stats.mastery ?? 0}%` }}
        />
      </div>
      <div className="chapter-card-stats">
        {stats.unresolvedWrong > 0 && (
          <span className="chapter-card-stat-warning">
            <AlertTriangle size={11} aria-hidden="true" /> {stats.unresolvedWrong} {t('dashboard.unresolvedShort')}
          </span>
        )}
        {stats.bookmarks > 0 && (
          <span><Bookmark size={11} aria-hidden="true" /> {stats.bookmarks} {t('dashboard.bookmarkedShort')}</span>
        )}
        {totalEssayProgress !== null && totalEssayProgress < 100 && (
          <span><ScrollText size={11} aria-hidden="true" /> {stats.essayDrafted}/{stats.totalEssays} {t('dashboard.essaysDrafted')}</span>
        )}
        {!Number.isNaN(stats.subjectiveAvgScore) && (
          <span className={stats.subjectiveNeedsRevisit > 0 ? 'chapter-card-stat-warning' : ''}>
            <ScrollText size={11} aria-hidden="true" />
            {' '}{t('dashboard.subjectiveAvg', { avg: stats.subjectiveAvgScore.toFixed(1) })}
            {stats.subjectiveNeedsRevisit > 0 && ` · ${stats.subjectiveNeedsRevisit} ${t('dashboard.subjectiveNeedsRevisit')}`}
          </span>
        )}
      </div>

      <BreakdownPanel rows={breakdownRows} t={t} />
    </div>
  );
}

// ── TopicCard ───────────────────────────────────────────────────────────────

export function TopicCard({
  stats, chapters, t,
}: {
  stats: TopicStats;
  chapters: ChapterStats[];
  t: TFunction;
}) {
  const parent = chapters.find((c) => c.chapterId === stats.parentChapterId);
  const breakdownRows: BreakdownRowDisplay[] = TOPIC_BREAKDOWN_ROWS
    .filter((r) => r.show(stats))
    .map((r) => ({
      family: r.family,
      icon: r.icon,
      label: t(r.labelKey),
      count: r.count(stats),
      to: r.to(stats),
      key: r.labelKey,
    }));
  return (
    <div className="topic-card" style={{ borderLeftColor: parent?.color ?? 'var(--color-ink-faint)' }}>
      <header className="topic-card-head">
        <span className="topic-card-parent">{parent?.chapterName ?? '—'}</span>
        <span className="topic-card-name">{stats.topicName}</span>
      </header>
      <div className="topic-card-stats">
        <span>{stats.totalItems} {t('dashboard.itemsShort')}</span>
        {stats.dueFlashcards > 0 && (
          <span><Layers size={11} aria-hidden="true" /> {stats.dueFlashcards} {t('dashboard.dueShort')}</span>
        )}
        {stats.unresolvedWrong > 0 && (
          <span className="chapter-card-stat-warning">
            <AlertTriangle size={11} aria-hidden="true" /> {stats.unresolvedWrong} {t('dashboard.unresolvedShort')}
          </span>
        )}
      </div>

      <BreakdownPanel rows={breakdownRows} t={t} />
    </div>
  );
}

// ── CognitiveBar ────────────────────────────────────────────────────────────

export function CognitiveBar({
  dist, t,
}: {
  dist: CognitiveDistribution[];
  t: TFunction;
}) {
  const total = dist.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;
  return (
    <div className="cog-bar">
      <div className="cog-bar-track">
        {dist.map((d) =>
          d.count > 0 ? (
            <div
              key={d.level}
              className={`cog-bar-segment cog-${d.level}`}
              style={{ width: `${(d.count / total) * 100}%` }}
              title={`${cogLabel(d.level, t)}: ${d.count}`}
            />
          ) : null,
        )}
      </div>
      <div className="cog-bar-legend">
        {dist.filter((d) => d.count > 0).map((d) => (
          <span key={d.level} className="cog-bar-legend-item">
            <span className={`cog-bar-dot cog-${d.level}`} aria-hidden="true" />
            {cogLabel(d.level, t)} <span className="cog-bar-legend-count">{d.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

export function masteryClass(m: number | null): string {
  if (m === null) return 'mastery-none';
  if (m >= 80) return 'mastery-good';
  if (m >= 50) return 'mastery-ok';
  return 'mastery-low';
}

export function cogLabel(level: CognitiveLevel | 'unlabeled', t: TFunction): string {
  switch (level) {
    case 'recall': return t('dashboard.cogRecall');
    case 'understand': return t('dashboard.cogUnderstand');
    case 'apply': return t('dashboard.cogApply');
    case 'analyze': return t('dashboard.cogAnalyze');
    case 'evaluate': return t('dashboard.cogEvaluate');
    case 'unlabeled': return t('dashboard.cogUnlabeled');
  }
}

export function pickLabel(id: ComponentId, locale: 'zh' | 'en'): string {
  const m = COMPONENT_MANIFEST.find((e) => e.id === id);
  return m ? m.label[locale] : id;
}
